// Anti-fragility helpers — prevent runaway loops & DOM thrashing.

const callBuckets = new Map();
const THROTTLE_WINDOW_MS = 1000;
const THROTTLE_MAX_CALLS = 30;
const blockedKeys = new Set();

/**
 * Run a DOM-modifying function safely. If the same `key` is invoked more than
 * THROTTLE_MAX_CALLS times within THROTTLE_WINDOW_MS, further calls are
 * silently dropped and a single warning is emitted in the console.
 *
 * Use for any side-effect that might be triggered by mutation observers,
 * resize observers, scroll handlers, etc.
 */
export function safeDomWrite(key, fn) {
  if (blockedKeys.has(key)) return;

  const now = Date.now();
  const bucket = callBuckets.get(key) || [];
  const recent = bucket.filter((t) => now - t < THROTTLE_WINDOW_MS);
  recent.push(now);
  callBuckets.set(key, recent);

  if (recent.length > THROTTLE_MAX_CALLS) {
    blockedKeys.add(key);
    // Auto-unlock after a cool-down so the app can recover.
    setTimeout(() => {
      blockedKeys.delete(key);
      callBuckets.set(key, []);
    }, 5000);
    // eslint-disable-next-line no-console
    console.warn(
      `[VIDOC safeGuard] DOM write '${key}' throttled (>${THROTTLE_MAX_CALLS}/s). Cooling down 5s.`,
    );
    return;
  }
  try {
    fn();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[VIDOC safeGuard] '${key}' threw:`, e);
  }
}

/** Global error logger — keeps a small ring buffer accessible in dev tools. */
const errorLog = [];
window.__VIDOC_ERRORS = errorLog;

export function installGlobalErrorHandlers() {
  window.addEventListener("error", (e) => {
    errorLog.push({
      kind: "error",
      time: new Date().toISOString(),
      message: e?.message,
      filename: e?.filename,
      line: e?.lineno,
    });
    if (errorLog.length > 50) errorLog.shift();
  });
  window.addEventListener("unhandledrejection", (e) => {
    errorLog.push({
      kind: "rejection",
      time: new Date().toISOString(),
      reason: String(e?.reason),
    });
    if (errorLog.length > 50) errorLog.shift();
  });
}
